<template>
  <div
    class="copilot-widget"
    :class="{ active: promptContent }"
    :style="{ top: `${position.y}px` }"
  >
    <div class="head" @mousedown="startDrag" @click="handleClick">
      <img class="copilot" src="@/image/copilot.gif" draggable="false" />
    </div>
    <div
      class="prompt"
      :class="{ active: promptContent || errorFilterMode.isOpen }"
    >
      <p class="p-r">
        智能QA：
        <i
          v-if="isInitiative"
          class="p-a right-0 c-p el-icon-close"
          @click="closePrompt"
        ></i>
      </p>
      <div v-html="promptContent" />
      <el-button
        v-if="!errorFilterMode.isOpen"
        type="primary"
        @click="openFilterMode"
      >
        立即查看
      </el-button>
      <el-button v-else type="primary" @click="closeFilterMode">
        退出筛选状态
      </el-button>
    </div>
  </div>
</template>

<script>
import { getQcCheckTips, closeQcCheckTips } from '@/api/qc/qcflow';
import { UNIFORM_ERRORS_MAP } from './const';
import { statistics } from '@/api/dot/flow';
import { COPILOT_PROMPT_TRACKS } from './report';

export default {
  name: 'CopilotWidget',
  props: ['errorFilterMode', 'orderId', 'qcTaskId', 'staParams'],
  data: () => {
    return {
      isDragging: false,
      isMoved: false,
      position: {
        y: 145,
      },
      offset: { y: 0 },
      prompt: {
        typeName: '',
        errorType: '',
      },
    };
  },
  computed: {
    promptContent() {
      const { isOpen: isFilterModeOpen, errorItem } = this.errorFilterMode;
      if (isFilterModeOpen) {
        return `已筛选全部<span class="highlight">${errorItem?.shortName}</span>项`;
      } else {
        const { errorType, typeName } = this.prompt;
        switch (errorType) {
          case 'highTerm':
            return `发现高频词<span class="highlight">${typeName}</span>可能存在多个译法`;
          case 'highSimilar':
            return '找到多个相似的句子，请注意重点审核哦';
          case 'highCritical':
            return `已经为您整理好所有<span class="highlight">${typeName}</span>的句子，请注意重点审核哦`;
          default:
            return '';
        }
      }
    },
    isInitiative() {
      // 主动弹窗则不显示关闭按钮
      return !this.errorFilterMode.isOpen;
    },
  },
  mounted() {
    this.updatePrompt();
  },
  methods: {
    handleClick() {
      !this.isMoved && this.$emit('click');
    },
    closePrompt() {
      this.$confirm(
        '关闭QA智能提示后，您仍然可以通过transkit图标查阅错误提示哦！',
        '提示',
        {
          distinguishCancelAndClose: true,
          confirmButtonText: '此类问题不再提示',
          cancelButtonText: '所有问题不再提示',
        },
      )
        .then(() => {
          this.closeTips(this.prompt.errorType);
        })
        .catch(() => {
          this.closeTips('ALL');
        });
    },
    closeTips(lowErrorType) {
      closeQcCheckTips({
        orderId: this.orderId,
        qcTaskId: this.qcTaskId,
        lowErrorType: lowErrorType,
      }).then(() => this.updatePrompt());
    },
    startDrag(event) {
      this.isDragging = true;
      this.isMoved = false;
      this.offset.y = event.clientY - this.position.y;
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(event) {
      if (this.isDragging) {
        this.isMoved = true;
        const minY = 0; // 上边界
        const maxY = window.innerHeight - 50; // 下边界
        const y = event.clientY - this.offset.y;
        if (y < minY) {
          this.position.y = minY;
        } else if (y > maxY) {
          this.position.y = maxY;
        } else {
          this.position.y = y;
        }
      }
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    openFilterMode() {
      const { errorType } = this.prompt;
      this.$emit('openFilterMode', {
        type: errorType,
        name: UNIFORM_ERRORS_MAP[errorType].name,
        shortName: UNIFORM_ERRORS_MAP[errorType].shortName,
      });

      closeQcCheckTips({
        orderId: this.orderId,
        qcTaskId: this.qcTaskId,
      }).then(() => this.updatePrompt());

      statistics(
        Object.assign({}, this.staParams, COPILOT_PROMPT_TRACKS.viewClick, {
          event_params: {
            copilotType: errorType,
          },
        }),
      );
    },
    closeFilterMode() {
      this.$emit('closeFilterMode');
      this.updatePrompt();
    },
    updatePrompt() {
      if (this.errorFilterMode.isOpen) return;
      return getQcCheckTips({
        orderId: this.orderId,
        qcTaskId: this.qcTaskId,
      }).then((res) => {
        const keyData = res.data?.data ?? {};
        this.prompt = keyData;

        if (Object.keys(keyData).length) {
          statistics(
            Object.assign(
              {},
              this.staParams,
              COPILOT_PROMPT_TRACKS.promptExpose,
            ),
          );
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.copilot-widget {
  z-index: 1;
  user-select: none;
  position: fixed;
  right: -73px;
  border-radius: 50px;
  cursor: pointer;
  transition: right 0.3s;
  .head {
    font-size: 16px;
    display: flex;
    align-items: center;
  }
  img.copilot {
    width: 130px;
    user-select: none;
  }

  &.active,
  &:hover {
    right: 0;
  }

  .prompt {
    position: absolute;
    top: 110%;
    right: -300px;
    width: 300px;
    padding: 10px;
    background-color: #fff;
    border-radius: 5px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    transition: right 0.3s;

    p {
      font-size: 16px;
      margin-bottom: 10px;
    }
    div {
      color: #666;
      margin-bottom: 10px;
      :deep(.highlight) {
        color: #ed7266 !important;
      }
    }
    button {
      width: 100%;
    }
    &.active {
      right: 0;
    }
  }
}
</style>
